import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Button, Input } from '@hsc/ui';
import { useAuth } from '@hsc/auth';
import { login } from '../shared/api/auth.js';
import { writeLocalAccessToken, writeLocalRefreshToken } from '../lib/storage.js';
import { PublicTrustFooter } from '../shared/ui/PublicTrustFooter.js';

// #87 (вход персонала по логину): персонал вводит ТОЛЬКО логин — локальную
// часть до «@» («manager», «housekeeper», «restaurant»), без почты. Бэкенд
// резолвит логин в полную почту в рамках текущего тенанта (по поддомену).
// Полная почта тоже принимается (обратная совместимость / platform-admin),
// поэтому валидация поля — просто непустое значение, без email-формата.
const schema = z.object({
  login: z.string().trim().min(1, 'Введите логин'),
  password: z.string().min(8, 'Минимум 8 символов'),
});

type FormValues = z.infer<typeof schema>;

const SUBMIT_ERROR_ID = 'login-error';

/**
 * Переводит ошибку запроса логина в человеко-понятное RU-сообщение.
 *
 * Best-practice UX: при любой ошибке форма ОСТАЁТСЯ на месте, сообщение
 * показывается инлайн под полями (над кнопкой) — без баннеров поверх страницы
 * и без «схлопывания» формы.
 */
function loginErrorMessage(e: unknown): string {
  const err = e as { status?: number; body?: { error?: string } };
  if (err.status === 401) {
    return 'Неверный логин или пароль';
  }
  if (err.status === 409 && err.body?.error === 'concurrent_session_limit') {
    return 'Превышен лимит активных сессий (5). Завершите одну из них и войдите снова.';
  }
  if (err.status === 429) {
    return 'Слишком много попыток входа. Подождите немного и попробуйте снова.';
  }
  if (typeof err.status === 'number' && err.status >= 500) {
    return 'Сервис временно недоступен. Попробуйте войти чуть позже.';
  }
  if (typeof err.status === 'number') {
    return 'Не удалось войти. Проверьте данные и попробуйте снова.';
  }
  // Нет status → сеть/таймаут (fetch reject).
  return 'Не удалось войти — проверьте соединение и попробуйте позже.';
}

/**
 * Страница входа. Переиспользует D5b-компоненты `@hsc/ui` (Input/Button) и
 * `@hsc/auth` для хранения токена. После успешного логина уходит на `/` —
 * корневой роут сам подтянет /v1/platform/me и направит по роли.
 *
 * UX по best-practice (волна 3 «Облака»):
 *  - на время запроса поля и кнопка блокируются (guard от двойного сабмита),
 *    кнопка показывает спиннер + «Вход…» (aria-busy);
 *  - при ошибке форма НЕ схлопывается: инлайн-сообщение под полями, поля
 *    разблокируются, введённый логин сохраняется, фокус уходит на пароль,
 *    у полей выставляется aria-invalid + aria-describedby;
 *  - при успехе queryClient.clear() (сброс кэша роли) и редирект на `/`.
 */
export function LoginRoute(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { login: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    // Guard от двойного сабмита: пока летит запрос — игнорируем повтор.
    if (isSubmitting) return;
    setSubmitError(null);
    try {
      // Бэкенд принимает поле email и как логин (локальная часть), и как
      // полную почту — резолвит в рамках тенанта по поддомену (#87).
      const res = await login({ email: values.login, password: values.password });
      writeLocalAccessToken(res.access_token);
      if (res.refresh_token) writeLocalRefreshToken(res.refresh_token);
      // Сброс кэша React Query: иначе при смене роли (logout→login другим)
      // /v1/platform/me отдаёт роль предыдущего юзера до refresh.
      queryClient.clear();
      auth.login(res.access_token);
      // Корневой роут резолвит роль из /v1/platform/me и редиректит на
      // нужный дашборд — здесь просто уходим на корень.
      navigate('/', { replace: true });
    } catch (e) {
      // Форма остаётся на месте — показываем инлайн-ошибку и возвращаем фокус
      // на поле пароля (логин сохранён, поля разблокируются автоматически).
      setSubmitError(loginErrorMessage(e));
      setFocus('password');
    }
  });

  // Поля помечаем невалидными, когда есть общая ошибка входа (401 и пр.) —
  // тянем их к инлайн-сообщению через aria-describedby.
  const hasSubmitError = submitError !== null;
  const submitErrorAria = hasSubmitError
    ? ({ 'aria-invalid': true, 'aria-describedby': SUBMIT_ERROR_ID } as const)
    : ({} as const);

  // По умолчанию страница входа — для ГОСТЯ (вход для гостя сверху, под ним
  // анонимный вход, ниже — кнопка «Вход для персонала», раскрывающая форму
  // логин/пароль). Персонал — вторичный сценарий.
  const [staffMode, setStaffMode] = useState(false);

  return (
    <main className="hsc-login">
      <div className="hsc-login__box" data-testid="login-card">
        <div className="hsc-login__brand">
          <span aria-hidden="true" className="hsc-login__logo">L</span>
          <h1 className="hsc-login__title">Lovart Hotels</h1>
          <p className="hsc-login__subtitle">Вход в рабочее пространство</p>
        </div>
        {!staffMode ? (
          // ДЕФОЛТ — вход для гостя сверху, аноним, затем кнопка персонала.
          <div className="hsc-login__anon">
            <Link to="/guest-login" className="hsc-login__anon-link">
              <Button type="button" variant="primary" size="lg" data-testid="guest-login-button">
                Вход для гостя
              </Button>
            </Link>
            <p className="hsc-login__anon-hint">
              Войти по данным заселения — фамилия, номер и даты
            </p>
            <Link to="/anonymous" className="hsc-login__anon-link">
              <Button type="button" variant="secondary" size="lg" data-testid="anonymous-button">
                Анонимный вход
              </Button>
            </Link>
            <p className="hsc-login__anon-hint">
              Посмотреть номера, афишу и оставить заявку без регистрации
            </p>
            <span className="hsc-login__divider">или</span>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              data-testid="staff-login-button"
              onClick={() => setStaffMode(true)}
            >
              Вход для персонала
            </Button>
          </div>
        ) : (
          // Персонал — раскрытая форма логин/пароль + возврат к гостю.
          <>
            <form onSubmit={onSubmit} noValidate>
              <div className="hsc-stack">
                <Input
                  label="Логин"
                  type="text"
                  autoComplete="username"
                  placeholder="manager"
                  disabled={isSubmitting}
                  {...register('login')}
                  {...(errors.login ? { error: errors.login.message } : {})}
                  {...(errors.login ? {} : submitErrorAria)}
                />
                <Input
                  label="Пароль"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Минимум 8 символов"
                  disabled={isSubmitting}
                  {...register('password')}
                  {...(errors.password ? { error: errors.password.message } : {})}
                  {...(errors.password ? {} : submitErrorAria)}
                />
                {submitError && (
                  <div
                    id={SUBMIT_ERROR_ID}
                    role="alert"
                    aria-live="assertive"
                    className="hsc-login__error"
                    data-testid="login-error"
                  >
                    {submitError}
                  </div>
                )}
                <Button type="submit" variant="primary" size="lg" loading={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="hsc-login__spinner" aria-hidden="true" />
                      Вход…
                    </>
                  ) : (
                    'Войти'
                  )}
                </Button>
              </div>
            </form>
            <div className="hsc-login__anon">
              <button
                type="button"
                className="ta-back"
                data-testid="back-to-guest"
                onClick={() => setStaffMode(false)}
              >
                ‹ Вход для гостя
              </button>
            </div>
          </>
        )}
        <PublicTrustFooter />
      </div>
    </main>
  );
}
